/**
 * src/hooks/useMyCommQuestions.js
 *
 * The signed-in person's own human-loop rows (ACEA-05): questions they asked
 * and tasks assigned to them, from the comm-questions endpoint. One store per
 * (tenant, person), shared by the ◎ badge and the Action Centre so the two
 * read the same list — the same shape as useMyBugUpdates.
 *
 * Nothing here is another person's (COMM-B-011): the server returns only rows
 * the caller asked or is assigned, and each row carries only the view that
 * role may see. A failed read keeps the last list and marks the store failed,
 * so a surface that counts the list can say it is unknown rather than empty
 * (useMyCommQuestionsStatus, GH #7583); the rest of the Action Centre never
 * waits for it.
 */

import { useEffect, useSyncExternalStore } from 'react';
import { listMyCommQuestions } from '../api/storage-azure.js';

const EMPTY = [];
const stores = new Map();

function getStore(key) {
  let store = stores.get(key);
  if (!store) {
    store = {
      key,
      questions: EMPTY,
      listeners: new Set(),
      loaded: false,
      failed: false,
      inflight: null,
    };
    stores.set(key, store);
  }
  return store;
}

function refresh(store) {
  if (store.inflight) return store.inflight;
  store.inflight = Promise.resolve()
    .then(listMyCommQuestions)
    .then((res) => {
      store.questions = Array.isArray(res?.questions) ? res.questions : EMPTY;
      store.loaded = true;
      store.failed = false;
      for (const l of store.listeners) l();
    })
    .catch((e) => {
      // A 403 (a portal role, or a caller not in the tenant's users) has no
      // questions to read — the same rule as the AI-assessment store.
      if (e?.httpStatus === 403 || /^Forbidden/.test(e?.message || '')) {
        store.questions = EMPTY;
        store.loaded = true;
        store.failed = false;
      } else {
        store.failed = true;
      }
      for (const l of store.listeners) l();
    })
    .finally(() => {
      store.inflight = null;
    });
  return store.inflight;
}

/** Re-read every held list — after an answer, feedback, withdrawal or a new question. */
export function refreshMyCommQuestions() {
  return Promise.all([...stores.values()].map(refresh));
}

function storeFor(session, tenant) {
  const tenantId = tenant?.id || session?.tenantId;
  const userId = session?.userId;
  // A super-admin's session is not a tenant person: the endpoint refuses it.
  const enabled = Boolean(tenantId && userId && session?.role !== 'super-admin');
  return enabled ? getStore(`${tenantId}::${userId}`) : null;
}

/**
 * @param {object} session  the viewer's session
 * @param {object} tenant   the current tenant; nothing is fetched without one
 * @param {{ refreshOnMount?: boolean }} [opts]
 * @returns {object[]} the person's views (role 'asker' | 'assignee')
 */
export function useMyCommQuestions(session, tenant, { refreshOnMount = false } = {}) {
  const store = storeFor(session, tenant);

  const questions = useSyncExternalStore(
    (cb) => {
      if (!store) return () => {};
      store.listeners.add(cb);
      return () => store.listeners.delete(cb);
    },
    () => (store ? store.questions : EMPTY)
  );

  useEffect(() => {
    if (!store) return;
    if (refreshOnMount || !store.loaded) refresh(store);
  }, [store, refreshOnMount]);

  return questions;
}

/** Test seam. */
export function __resetCommQuestionStores() {
  stores.clear();
}

/**
 * How far the list from useMyCommQuestions can be counted (GH #7583):
 * 'ok' — read; 'loading' — not read yet; 'failed' — the last read failed (the
 * list is the last one read, or none); 'none' — this session has no questions
 * to read (a super-admin), so an empty list is the whole answer.
 */
export function useMyCommQuestionsStatus(session, tenant) {
  const store = storeFor(session, tenant);
  return useSyncExternalStore(
    (cb) => {
      if (!store) return () => {};
      store.listeners.add(cb);
      return () => store.listeners.delete(cb);
    },
    () => (!store ? 'none' : store.failed ? 'failed' : store.loaded ? 'ok' : 'loading')
  );
}
